import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { usePutPortfolioManualValueLoansMutation } from "web_myplann/Api/Portfolio/Mutations/usePutPortfolioManualValueLoansMutation";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { getPortfolioValueDate } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

type Props = {
	open: boolean;
	onClose: () => void;
	portfolio: PortfolioDetailResponseDTO;
	title: string;
	currencySign: string;
};

type ValueFormModel = { value: number; date: Date };

const getDefaultValues = () => ({ date: new Date() }) as ValueFormModel;

export const PortfolioValueDrawer: FC<Props> = ({
	open,
	onClose,
	portfolio,
	title,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const { mutate, isPending } = usePutPortfolioManualValueLoansMutation();
	const { data: reporting } = usePortfolioIdReportingQuery(portfolio.id);

	const { daysData, totalValue } = calculateGraphData(
		reporting?.reportingData,
		reporting?.summary ?? undefined,
	);
	const showPreviousValue = daysData.length > 0;

	const schema = z.object({
		value: z
			.number({
				required_error: t(Resources.Portfolio.AddShared.Validations.required),
				invalid_type_error: t(
					Resources.Portfolio.AddShared.Validations.required,
				),
			})
			.gt(
				0,
				t(Resources.PortfolioDetail.SharedLabels.ValueDrawer.positiveValue),
			)
			.max(
				ValidationLimits.Amount.Max,
				t(Resources.Portfolio.AddShared.Validations.maxValue),
			),
		date: z.date({
			required_error: t(
				Resources.PortfolioDetail.Loan.ValueDrawer.requiredDate,
			),
			invalid_type_error: t(
				Resources.PortfolioDetail.Loan.ValueDrawer.requiredDate,
			),
		}),
	});

	const { control, handleSubmit, reset } = useForm<ValueFormModel>({
		resolver: zodResolver(schema),
		defaultValues: getDefaultValues(),
	});

	useEffect(() => {
		if (open) {
			reset(getDefaultValues());
		}
	}, [open, reset]);

	const onSubmit = handleSubmit((formValues) => {
		mutate(
			{
				portfolioId: portfolio.id,
				request: {
					value: formValues.value,
					date: format(formValues.date, DateFormat.Iso),
				},
			},
			{
				onSuccess: () => {
					toast.success(
						t(Resources.PortfolioDetail.SharedLabels.ValueDrawer.savedToast),
					);
					onClose();
				},
			},
		);
	});

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(
				showPreviousValue
					? Resources.PortfolioDetail.SharedLabels.ValueDrawer.updateTitle
					: Resources.PortfolioDetail.SharedLabels.ValueDrawer.fillTitle,
			)}
		>
			<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
				<Stack
					gap={theme.spacingTokens.md}
					px={theme.spacingTokens.lg}
					pb={theme.spacingTokens.md}
				>
					<Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
						{title}
					</Typography>
					{showPreviousValue && (
						<Typography variant="body2" color={theme.palette.grey[400]} tabular>
							{t(
								Resources.PortfolioDetail.SharedLabels.ValueDrawer
									.previousValue,
							)}{" "}
							<Typography
								component="span"
								variant="bodyMedium"
								color={theme.palette.grey[600]}
								tabular
							>
								{moneyFormatter(totalValue, currencySign, "", 2)}
							</Typography>{" "}
							<Typography
								component="span"
								variant="body2"
								color={theme.palette.grey[300]}
								tabular
							>
								({getPortfolioValueDate(daysData, portfolio)})
							</Typography>
						</Typography>
					)}
				</Stack>
				<FlatRowFormInput
					control={control}
					name="value"
					type="number"
					label={t(Resources.PortfolioDetail.SharedLabels.ValueDrawer.newValue)}
					placeholder={t(
						Resources.PortfolioDetail.SharedLabels.ValueDrawer.placeholder,
					)}
					suffix={currencySign}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="date"
					label={t(Resources.PortfolioDetail.Loan.ValueDrawer.date)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					maxDate={new Date()}
					isLast
				/>
				<Stack px={theme.spacingTokens.lg} pt={theme.spacingTokens.lg}>
					<Button type="submit" variant="primary" loading={isPending}>
						{t(Resources.PortfolioDetail.SharedLabels.ValueDrawer.confirm)}
					</Button>
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
